import React, { useEffect, useMemo, useState } from 'react';
import classnames from 'classnames/bind';
import { Slide as SlideAnimation } from 'react-awesome-reveal';
import { HeaderV6 } from '../HeaderV6';
import { Button } from '../Button';
import { QuizProgressBar } from '../QuizProgressBar';
import { QuestionAnswerSlideV6 } from '../QuestionAnswerSlideV6';
import { QuestionMultiAnswerSlideV4 } from '../QuestionMultiAnswerSlideV4';
import { StartSlideV19Experiment } from '../StartSlideV19Experiment';
import { PauseSlide } from '../PauseSlide';
import { ProductSlide } from '../ProductSlide';
import { ReviewSlideV2 } from '../ReviewSlideV2';
import { ProfileSlideV2 } from '../ProfileSlideV2';
import { PlanLoaderSlideV8 } from '../PlanLoaderSlideV8';
import { EmailSlideV2 } from '../EmailSlideV2';
import { PlanGraphSlideV6 } from '../PlanGraphSlideV6';
import { SaleV31 } from '../SaleV31';
import { TimerV2 } from '../TimerV2';
import { FooterV2 } from '../FooterV2';
import { AgreementText } from '../AgreementText';
import { useQuiz } from '../../shared/use-quiz';
import { useUserProperties } from '../../shared/use-user-properties';
import { useLegalInfo } from '../../shared/use-legal-info';
import { useSlideEvents } from '../../shared/use-slide-events';
import { isExpiredPromo } from '../../shared/promo-helpers';
import { getAnswersFromSource } from '../../shared/answer-helpers';
import { getPropsFromSourceAnswer } from '../../shared/personalization-helpers';
import {
    EMAIL_SLIDE,
    PAUSE_SLIDE,
    PLAN_GRAPH_SLIDE,
    PLAN_LOADER_SLIDE,
    PRODUCT_SLIDE,
    PROFILE_SLIDE,
    QUESTION_ANSWER_SLIDE_COMPONENT,
    QUESTION_MULTI_ANSWER_SLIDE_COMPONENT,
    REVIEW_SLIDE,
    START_SLIDE
} from '../../shared/quiz-component-types';
import { preloadImages } from '../../../lib/shared/preload-images';
import emojiSpriteSrc from '../../assets/images/emoji-sprites/emoji-sprite-v53-2.png';
import pauseArmchairTabletSrc from '../../assets/images/slide/pause-armchair-tablet.png';
import pausePhoneRecordingSrc from '../../assets/images/slide/pause-phone-recording.png';
import productTabletMemoirSrc from '../../assets/images/slide/product-tablet-memoir.png';
import planGraphTimelineSrc from '../../assets/images/slide/plan-graph-timeline.png';
import footerLogoSrc from '../../assets/images/boundly-logo-green-light.png';
import styles from './CommonPrelanderV44.css';
import '../../assets/images/emoji-sprites/emoji-sprite-v53-2.css';

const cx = classnames.bind(styles);

const IMAGES = [emojiSpriteSrc, `${pauseArmchairTabletSrc}?v=2`, pausePhoneRecordingSrc, productTabletMemoirSrc, `${planGraphTimelineSrc}?v=2`];

const SLIDE_COMPONENTS = {
    [QUESTION_ANSWER_SLIDE_COMPONENT]: QuestionAnswerSlideV6,
    [QUESTION_MULTI_ANSWER_SLIDE_COMPONENT]: QuestionMultiAnswerSlideV4,
    [START_SLIDE]: StartSlideV19Experiment,
    [PAUSE_SLIDE]: PauseSlide,
    [PRODUCT_SLIDE]: ProductSlide,
    [REVIEW_SLIDE]: ReviewSlideV2,
    [PROFILE_SLIDE]: ProfileSlideV2,
    [PLAN_LOADER_SLIDE]: PlanLoaderSlideV8,
    [EMAIL_SLIDE]: EmailSlideV2,
    [PLAN_GRAPH_SLIDE]: PlanGraphSlideV6
};

const DELAY = 350;

function CommonPrelanderV44({
    initialPrelander,
    quizSlides,
    ageBranchSlides
}) {
    const {
        currentSlideIndex,
        results,
        submitAnswer,
        submitTemporaryAnswer,
        temporaryAnswer,
        finished,
        nextSlide,
        backSlide,
        setEmail,
        setExpiryTimestampPromo
    } = useQuiz(quizSlides, initialPrelander);

    const ageAnswer = ageBranchSlides && results.userResponses.find(userResponse => userResponse.questionId === ageBranchSlides.questionId);
    const isAgeBranch = Boolean(ageAnswer) && ageBranchSlides.branchAnswerIds.includes(ageAnswer.id);

    const resolvedSlides = useMemo(
        () => (isAgeBranch ? quizSlides.map(slide => ageBranchSlides.slides[slide.id] || slide) : quizSlides),
        [quizSlides, ageBranchSlides, isAgeBranch]
    );

    const currentSlide = finished ? {} : resolvedSlides[currentSlideIndex];
    const quizSlidesIndexing = resolvedSlides.filter(slide => !slide.isNotIndexing);
    const isQuestionSlide = [QUESTION_ANSWER_SLIDE_COMPONENT, QUESTION_MULTI_ANSWER_SLIDE_COMPONENT].includes(currentSlide.component);
    const isStartSlide = currentSlide.component === START_SLIDE;
    const isEmailSlide = currentSlide.component === EMAIL_SLIDE;
    const indexedSlideNumber = quizSlidesIndexing.indexOf(currentSlide) + 1;
    const [isExpired, setIsExpired] = useState(isExpiredPromo(results.expiryTimestampPromo));
    const { legalRegion, legalLinks, legalEntity } = useLegalInfo();
    const [selectedSubscriptionData, selectSubscription] = useState({});
    const [merchantData, setMerchantData] = useState({});

    useUserProperties(initialPrelander);

    useEffect(() => {
        preloadImages(IMAGES);
    }, []);

    const { logQuizLifecycle } = useSlideEvents({ currentSlideIndex: indexedSlideNumber, results, currentSlide, quizSlides: resolvedSlides });

    const handleAnswerWithTransition = answer => () => {
        logQuizLifecycle(quizSlidesIndexing.length === indexedSlideNumber, answer);
        submitAnswer(answer);
    };

    const setExpiryTimestampDiscount = () => {
        setExpiryTimestampPromo();
        setIsExpired(false);
    };

    const handleContinue = () => {
        document.getElementById('root').scrollIntoView({ block: 'start', behavior: 'smooth' });
        logQuizLifecycle(quizSlidesIndexing.length === indexedSlideNumber, temporaryAnswer);

        if (currentSlideIndex === quizSlides.length - 1) {
            setExpiryTimestampDiscount();
        }

        nextSlide(temporaryAnswer);
    };

    const renderLeftNavigation = () => {
        if (finished) {
            if (!results.email || selectedSubscriptionData.plan) {
                return null;
            }

            return (
                <>
                    <TimerV2
                        expiryTimestamp={isExpired ? null : results.expiryTimestampPromo}
                        label='Discount is reserved for:'
                        className={styles.headerTimer}
                    />
                    <Button
                        label='Get my plan'
                        type='pine'
                        className={cx('buttonMyPlan', 'animation')}
                        onClick={() => document.getElementById('subscription-start-block')?.scrollIntoView({ block: 'start', behavior: 'smooth' })}
                    />
                </>
            );
        }

        return null;
    };

    const renderHeader = () => {
        if (merchantData?.merchant) {
            return null;
        }

        const hasBackButton = !finished && Boolean(currentSlideIndex) && !currentSlide?.isNotBackButton;
        const hasPromoHeader = finished && Boolean(results.email);

        return (
            <HeaderV6
                renderLeftNavigation={renderLeftNavigation}
                progressBar={isQuestionSlide && (
                    <QuizProgressBar
                        currentSlideIndex={indexedSlideNumber}
                        totalSlides={quizSlidesIndexing.length}
                    />
                )}
                questionNav={isQuestionSlide && {
                    category: currentSlide.props?.category,
                    hasCounter: true,
                    currentSlideIndex: indexedSlideNumber,
                    totalSlides: quizSlidesIndexing.length
                }}
                hasBackButton={hasBackButton}
                onBack={backSlide}
                className={cx('header', {
                    withBorder: finished,
                    withoutPromo: hasPromoHeader && isExpired,
                    withPromo: hasPromoHeader && !isExpired,
                    withPlan: Boolean(selectedSubscriptionData.plan)
                })}
                isFinished={finished && Boolean(results.email) && !selectedSubscriptionData.plan}
                url={window.location.pathname}
            />
        );
    };

    const renderFooter = () => {
        if (finished) {
            if (Boolean(results.email) && !selectedSubscriptionData.plan) {
                return <FooterV2 legalEntity={legalEntity} legalLinks={legalLinks} logoSrc={footerLogoSrc} />;
            }

            return null;
        }

        if (Boolean(currentSlide.continueButton)) {
            return (
                <SlideAnimation
                    delay={500}
                    duration={250}
                    triggerOnce
                    direction='up'
                    className={cx('slideFooter', { withBorder: currentSlide.component === QUESTION_MULTI_ANSWER_SLIDE_COMPONENT })}
                    key={currentSlide.id}
                >
                    <div className={styles.slideFooterWrapper}>
                        <Button
                            type='pine'
                            label={currentSlide.continueButton.label || 'Continue'}
                            onClick={handleContinue}
                            inSlide
                            disabled={currentSlide.component === QUESTION_MULTI_ANSWER_SLIDE_COMPONENT ? (temporaryAnswer.answerIds?.length || 0) < (currentSlide.continueButton?.minAnswers || 1) : false}
                        />
                    </div>
                </SlideAnimation>
            );
        }

        return null;
    };

    const renderContent = () => {
        if (finished) {
            return (
                <SaleV31
                    data={results}
                    expiryTimestamp={results.expiryTimestampPromo}
                    onExpirePromo={() => setIsExpired(true)}
                    isExpired={isExpired}
                    initialPrelander={initialPrelander}
                    selectSubscription={selectSubscription}
                    selectedSubscriptionData={selectedSubscriptionData}
                    merchantData={merchantData}
                    setMerchantData={setMerchantData}
                    legalLinks={legalLinks}
                    legalRegion={legalRegion}
                    isAgeBranch={isAgeBranch}
                />
            );
        }

        const Slide = SLIDE_COMPONENTS[currentSlide.component];
        const resultData = (temporaryAnswer?.id || temporaryAnswer?.answerIds)
            ? temporaryAnswer
            : results.userResponses.find(userResponse => userResponse.questionId === currentSlide.id);
        const dynamicAnswers = getAnswersFromSource(currentSlide, results);
        const personalizedProps = getPropsFromSourceAnswer(currentSlide, results);

        return (
            <div
                key={currentSlideIndex}
                className={cx('slide', {
                    withQuestion: isQuestionSlide,
                    withStartSlide: isStartSlide,
                    withEmailSlide: isEmailSlide,
                    withPlanLoaderSlide: currentSlide.component === PLAN_LOADER_SLIDE,
                    withProfileSlide: currentSlide.component === PROFILE_SLIDE
                })}
            >
                <Slide
                    {...currentSlide.props}
                    {...personalizedProps}
                    answers={dynamicAnswers || currentSlide.props?.answers}
                    spriteVersion='v53-2'
                    submitAnswer={handleAnswerWithTransition}
                    submitTemporaryAnswer={submitTemporaryAnswer}
                    userResponse={resultData}
                    onContinue={handleContinue}
                    id={currentSlide.id}
                    results={results}
                    delay={DELAY}
                    setEmail={setEmail}
                    legalLinks={legalLinks}
                    legalRegion={legalRegion}
                    isAgeBranch={isAgeBranch}
                />
                {isStartSlide && <AgreementText legalLinks={legalLinks} />}
            </div>
        );
    };

    return (
        <div className={styles.container}>
            <div className={cx({ stickyHeader: finished && Boolean(results.email) && !selectedSubscriptionData.plan })}>
                {renderHeader()}
            </div>
            <main
                key={finished}
                className={cx('main', {
                    centered: currentSlide.centered,
                    animated: finished || isEmailSlide
                })}
            >
                {renderContent()}
            </main>
            {renderFooter()}
        </div>
    );
}

export {
    CommonPrelanderV44
};
